/**
 * usePermissions Hook
 * 
 * Custom hook for checking user permissions throughout the application.
 * Works with the role-based access control (RBAC) system.
 */

import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import { useMemo, useCallback } from 'react';

// Permission action constants - should match backend PermissionAction enum
export const PermissionActions = {
  // User Management
  CREATE_USER: 'create_user',
  READ_USER: 'read_user',
  UPDATE_USER: 'update_user',
  DELETE_USER: 'delete_user',
  MANAGE_USER_ROLES: 'manage_user_roles',
  RESTORE_USER: 'restore_user',

  // Category Management
  CREATE_CATEGORY: 'create_category',
  READ_CATEGORY: 'read_category',
  UPDATE_CATEGORY: 'update_category',
  DELETE_CATEGORY: 'delete_category',
  RESTORE_CATEGORY: 'restore_category',

  // Stock/Inventory Management
  CREATE_STOCK: 'create_stock',
  READ_STOCK: 'read_stock',
  UPDATE_STOCK: 'update_stock',
  DELETE_STOCK: 'delete_stock',

  // Supplier Management
  CREATE_SUPPLIER: 'create_supplier',
  READ_SUPPLIER: 'read_supplier',
  UPDATE_SUPPLIER: 'update_supplier',
  DELETE_SUPPLIER: 'delete_supplier',
  RESTORE_SUPPLIER: 'restore_supplier',

  // Dashboard
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_ADMIN_DASHBOARD: 'view_admin_dashboard',

  // Sales
  CREATE_SALE: 'create_sale',
  VIEW_SALES: 'view_sales',
  PROCESS_REFUND: 'process_refund',
  DELETE_SALE: 'delete_sale',
  EDIT_SALE: 'edit_sale',
  MANAGE_PURCHASES: 'manage_purchases',

  // Billing-specific
  EDIT_BILLING_PRICE: 'edit_billing_price',
  EDIT_BILLING_DISCOUNT: 'edit_billing_discount',
  APPLY_CUSTOM_DISCOUNT: 'apply_custom_discount',
  VOID_TRANSACTION: 'void_transaction',
  HOLD_BILL: 'hold_bill',

  // Customer Management
  CREATE_CUSTOMER: 'create_customer',
  READ_CUSTOMER: 'read_customer',
  UPDATE_CUSTOMER: 'update_customer',
  DELETE_CUSTOMER: 'delete_customer',

  // Credit Management
  CREATE_CREDIT_PAYMENT: 'create_credit_payment',
  READ_CREDIT: 'read_credit',

  // Reports
  VIEW_SALES_REPORTS: 'view_sales_reports',
  VIEW_INVENTORY_REPORTS: 'view_inventory_reports',
  EXPORT_REPORTS: 'export_reports',

  // System Administration
  MANAGE_PERMISSIONS: 'manage_permissions',
  VIEW_SYSTEM_LOGS: 'view_system_logs',
  MANAGE_SYSTEM_SETTINGS: 'manage_system_settings',
} as const;

export type PermissionAction = typeof PermissionActions[keyof typeof PermissionActions];

interface UsePermissionsReturn {
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isCashier: boolean;
  role: string | undefined;
}

/**
 * Hook to check user permissions
 * 
 * @example
 * ```tsx
 * const { hasPermission, isSuperAdmin } = usePermissions();
 * 
 * // Check single permission
 * if (hasPermission(PermissionActions.EDIT_BILLING_PRICE)) {
 *   // Show price edit field
 * }
 * 
 * // Check multiple permissions (any)
 * if (hasAnyPermission([PermissionActions.CREATE_USER, PermissionActions.UPDATE_USER])) {
 *   // Show user management
 * }
 * ```
 */
export const usePermissions = (): UsePermissionsReturn => {
  const user = useSelector((state: RootState) => state.auth.user);
  
  // Get permissions - check effectivePermissions first (from RBAC), then legacy permissions, then role-based fallback
  const permissions = useMemo(() => {
    // First check for effectivePermissions from RBAC system (backend calculated)
    if (user?.effectivePermissions && Array.isArray(user.effectivePermissions) && user.effectivePermissions.length > 0) {
      // Extract action strings from effectivePermissions objects
      const permActions = user.effectivePermissions.map((p: any) => 
        typeof p === 'string' ? p : (p.action || p.name)
      ).filter(Boolean);
      if (permActions.length > 0) {
        return permActions;
      }
    }
    
    // Then check legacy permissions array
    if (user?.permissions && user.permissions.length > 0) {
      return user.permissions;
    }
    
    // Fallback: Generate default permissions based on role
    // This ensures UI works even before permissions are fetched
    if (user?.role) {
      return getDefaultPermissionsForRole(user.role);
    }
    
    return [];
  }, [user?.effectivePermissions, user?.permissions, user?.role]);

  const role = user?.role;

  // Check if user has superadmin wildcard permission
  const isSuperAdmin = useMemo(() => {
    return permissions.includes('*') || role === 'superadmin';
  }, [permissions, role]);

  const isAdmin = useMemo(() => {
    return role === 'admin' || isSuperAdmin;
  }, [role, isSuperAdmin]);

  const isManager = useMemo(() => {
    return role === 'manager' || isAdmin;
  }, [role, isAdmin]);

  const isCashier = useMemo(() => {
    return role === 'cashier';
  }, [role]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission: string): boolean => {
    // Superadmin has all permissions
    if (isSuperAdmin) {
      return true;
    }
    return permissions.includes(permission);
  }, [permissions, isSuperAdmin]);

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback((requiredPermissions: string[]): boolean => {
    if (isSuperAdmin) {
      return true;
    }
    return requiredPermissions.some(permission => permissions.includes(permission));
  }, [permissions, isSuperAdmin]);

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback((requiredPermissions: string[]): boolean => {
    if (isSuperAdmin) {
      return true;
    }
    return requiredPermissions.every(permission => permissions.includes(permission));
  }, [permissions, isSuperAdmin]);

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    isAdmin,
    isManager,
    isCashier,
    role,
  };
};

/**
 * Higher-order component for permission-based rendering
 */
export const withPermission = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission: string,
  FallbackComponent?: React.ComponentType
) => {
  return function WithPermissionComponent(props: P) {
    const { hasPermission } = usePermissions();
    
    if (!hasPermission(requiredPermission)) {
      return FallbackComponent ? <FallbackComponent /> : null;
    }
    
    return <WrappedComponent {...props} />;
  };
};

/**
 * Get default permissions based on user role (frontend fallback)
 * Used when server hasn't returned permissions yet
 */
function getDefaultPermissionsForRole(role: string): string[] {
  const rolePermissions: Record<string, string[]> = {
    superadmin: ['*'],
    admin: [
      'create_user', 'read_user', 'update_user', 'delete_user', 'manage_user_roles', 'restore_user',
      'create_category', 'read_category', 'update_category', 'delete_category', 'restore_category',
      'create_stock', 'read_stock', 'update_stock', 'delete_stock',
      'create_supplier', 'read_supplier', 'update_supplier', 'delete_supplier', 'restore_supplier',
      'view_dashboard', 'view_admin_dashboard',
      'create_sale', 'view_sales', 'process_refund',
      'edit_billing_price', 'edit_billing_discount', 'apply_custom_discount', 'void_transaction', 'hold_bill',
      'create_customer', 'read_customer', 'update_customer', 'delete_customer',
      'view_sales_reports', 'view_inventory_reports', 'export_reports',
      'manage_permissions', 'view_system_logs', 'manage_system_settings'
    ],
    manager: [
      'create_user', 'read_user', 'update_user',
      'create_category', 'read_category', 'update_category', 'delete_category',
      'create_stock', 'read_stock', 'update_stock', 'delete_stock',
      'create_supplier', 'read_supplier', 'update_supplier', 'delete_supplier',
      'view_dashboard',
      'create_sale', 'view_sales', 'process_refund',
      'edit_billing_price', 'edit_billing_discount', 'apply_custom_discount', 'hold_bill',
      'create_customer', 'read_customer', 'update_customer', 'delete_customer',
      'view_sales_reports', 'view_inventory_reports', 'export_reports'
    ],
    cashier: [
      'read_category', 'read_stock',
      'view_dashboard',
      'create_sale', 'view_sales',
      'edit_billing_price', 'edit_billing_discount',
      'hold_bill',
      'create_customer', 'read_customer', 'update_customer',
      'view_sales_reports'
    ],
    staff: [
      'read_category', 'read_stock',
      'view_dashboard',
      'read_customer'
    ],
    user: [
      'view_dashboard'
    ]
  };

  return rolePermissions[role] || rolePermissions.user;
}

export default usePermissions;
